import { useEffect, useState } from 'react';
import type { Order, OrderStatus } from '@cracked-mug/shared';
import {
  ORDER_STATUSES,
  ORDER_STATUS_LABELS,
  formatOrderNumber,
  formatPrice,
} from '@cracked-mug/shared';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { fetchAllOrders, cancelOrder } from '../../services/ordersApi';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { fieldClass } from '../../components/ui/fieldStyles';
import { cn } from '../../lib/cn';

export function AdminOrderLog() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchAllOrders({
      limit: 100,
      ...(statusFilter !== '' ? { status: statusFilter } : {}),
    })
      .then((data) => {
        if (cancelled) return;
        setOrders(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load orders');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  const handleCancel = async (order: Order): Promise<void> => {
    setPendingId(order.orderId);
    try {
      const updated = await cancelOrder(order.orderId);
      setOrders((prev) => prev.map((o) => (o.orderId === updated.orderId ? updated : o)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setPendingId(null);
    }
  };

  const exportCsv = (): void => {
    const header = 'orderNumber,placedAt,teacher,room,status,paymentStatus,subtotal,tax,tip,total';
    const rows = orders.map((o) =>
      [
        o.orderNumber,
        o.placedAt,
        `"${o.teacherName.replace(/"/g, '""')}"`,
        o.teacherRoom,
        o.status,
        o.paymentStatus,
        (o.subtotalCents / 100).toFixed(2),
        (o.taxCents / 100).toFixed(2),
        (o.tipCents / 100).toFixed(2),
        (o.totalCents / 100).toFixed(2),
      ].join(','),
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cracked-mug-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl">Orders</h1>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as OrderStatus | '')}
            className={cn(fieldClass, 'w-auto py-2')}
          >
            <option value="">All statuses</option>
            {ORDER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {ORDER_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={exportCsv}
            disabled={orders.length === 0}
            className="inline-flex items-center gap-1.5 rounded-full border border-wca-gray-light bg-wca-surface px-4 py-2 text-sm font-medium text-wca-black transition-colors hover:border-wca-red hover:text-wca-red disabled:opacity-50 disabled:hover:border-wca-gray-light disabled:hover:text-wca-black"
          >
            <Download size={15} aria-hidden="true" />
            Export CSV
          </button>
        </div>
      </header>

      {error !== null && (
        <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3">{error}</p>
      )}

      {isLoading ? (
        <p className="font-mono text-sm text-wca-gray-mid py-12">Loading orders…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-wca-black/[0.06] bg-wca-surface shadow-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-wca-gray-light bg-wca-chalk/60 text-left font-mono text-[11px] uppercase tracking-wider text-wca-gray-mid">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">Teacher</th>
                <th className="px-4 py-3 font-semibold">Room</th>
                <th className="px-4 py-3 font-semibold">Items</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Total</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const itemCount = order.items.reduce((s, i) => s + i.quantity, 0);
                const date = new Date(order.placedAt);
                return (
                  <tr
                    key={order.orderId}
                    className="border-b border-wca-gray-light last:border-b-0 transition-colors hover:bg-wca-chalk/50"
                  >
                    <td className="px-4 py-3 font-mono tabular-nums">
                      <Link to={`/orders/${order.orderId}`} className="hover:text-wca-red">
                        {formatOrderNumber(order.orderNumber)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-wca-gray-mid whitespace-nowrap">
                      {date.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3">{order.teacherName}</td>
                    <td className="px-4 py-3 font-mono">{order.teacherRoom}</td>
                    <td className="px-4 py-3">{itemCount}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="px-4 py-3 font-mono text-wca-red tabular-nums">
                      {formatPrice(order.totalCents)}
                    </td>
                    <td className="px-4 py-3">
                      {order.status !== 'cancelled' && order.status !== 'completed' && (
                        <button
                          type="button"
                          onClick={() => void handleCancel(order)}
                          disabled={pendingId === order.orderId}
                          className="rounded-full px-3 py-1.5 text-xs font-medium text-wca-gray-mid transition-colors hover:bg-wca-gray-light hover:text-wca-red disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-wca-gray-mid">
                    No orders match the filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
